import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useNavigate } from 'react-router-dom';
// ─────────────────────────────────────────────────────────────────────────────
// Constants (static; avoid re-creation)
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_CONFIG = Object.freeze({
    height: 480,
    initialScale: 0.8,
    minScale: 0.5,
    maxScale: 8,
    step: 0.2,
    animationDuration: 200,
    easingFunction: 'easeOut',
});

const STATUS_COLORS = Object.freeze({
    green: 'rgba(62, 205, 91, 0.94)',
    yellow: '#f59e0b',
    red: '#ef4444',
    gray: '#6b7280',
    default: 'rgba(37, 100, 235, 0)',
});

const THEME = Object.freeze({
    primary: '#000000ff',
    primaryRing: 'rgba(37,99,235,0.35)',
    statusRings: Object.freeze({
        green: 'rgba(16,185,129,0.35)',
        yellow: 'rgba(245,158,11,0.35)',
        red: 'rgba(239,68,68,0.35)',
        gray: 'rgba(107,114,128,0.35)',
        default: '#000000ff',
    }),
});

const LABEL_DIMENSIONS = Object.freeze({
    width: 84,
    height: 32,
    minGap: 10,
});

const ANCHOR_BOX = Object.freeze({
    size: 18,
    padding: 3,
});

// ─────────────────────────────────────────────────────────────────────────────
// Styles (inline to keep self‑contained)
// ─────────────────────────────────────────────────────────────────────────────
const styles = Object.freeze({
    container: { position: 'relative', width: '100%' },
    wrapper: {
        width: '100%',
        height: '100%',
        touchAction: 'none',
        background: '#f5f5f5',
        overflow: 'hidden',
    },
    content: {
        userSelect: 'none',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        display: 'block',
        maxWidth: '100%',
        maxHeight: '100%',
        height: 'auto',
        width: 'auto',
        pointerEvents: 'none',
    },
    clickOverlay: { position: 'absolute', inset: 0, cursor: 'crosshair' },
    controlPanel: {
        position: 'absolute',
        right: 12,
        bottom: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 10,
    },
    button: {
        width: 42,
        height: 42,
        borderRadius: 12,
        border: '1px solid rgba(0,0,0,0.15)',
        background: '#fff',
        cursor: 'pointer',
        fontSize: 18,
        lineHeight: '42px',
        textAlign: 'center',
        boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
        userSelect: 'none',
        transition: 'all 0.2s ease',
    },
    lockButton: { fontSize: 12, color: '#fff' },
});

// Inject once; keyframes fixed to avoid duplicate 100% blocks
const injectBlinkStyles = () => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('zoomable-blink-styles')) return;
    const style = document.createElement('style');
    style.id = 'zoomable-blink-styles';
    style.textContent = `
    @keyframes marker-blink { 0%{opacity:1} 50%{opacity:.35} 100%{opacity:1} }
    .blink-yellow { animation: marker-blink .9s steps(2, start) infinite; }
    .blink-red    { animation: marker-blink .7s steps(2, start) infinite; }
  `;
    document.head.appendChild(style);
};

const getMarkerColors = (name, alertStatus, statusByName) => {
    if (!alertStatus || !Array.isArray(alertStatus)) {
        return {
            color: STATUS_COLORS.default,
            ring: THEME.statusRings.default,
            status: 'default',
        };
    }
    const status = statusByName?.get?.(name) || 'default';
    return {
        color: STATUS_COLORS[status] || STATUS_COLORS.default,
        ring: THEME.statusRings[status] || THEME.statusRings.default,
        status,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────
const useMarkers = (initial = [], onChange) => {
    const toNumber = (val) => {
        if (val === null || val === undefined) return null;
        if (typeof val === 'string') {
            const n = Number(val.trim());
            return Number.isFinite(n) ? n : null;
        }
        if (typeof val === 'number') return Number.isFinite(val) ? val : null;
        return null;
    };

    const pickCoord = (item) => {
        const xCand = item.x ?? item.latitude ?? item.lat;
        const yCand = item.y ?? item.longitude ?? item.lng ?? item.lon;
        const x = toNumber(xCand);
        const y = toNumber(yCand);
        return { x, y };
    };

    const parseInitial = useCallback((list) => {
        if (!Array.isArray(list)) return [];
        return list
            .map((item) => {
                const { x, y } = pickCoord(item);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
                return {
                    id: item.id ?? `base-${Date.now()}-${Math.random()}`,
                    x,
                    y,
                    name: item.name,
                    type: item.type || (item.name?.includes('ALC') ? 'ALC' : 'OW'),
                };
            })
            .filter(Boolean);
    }, []);

    const [baseMarkers, setBaseMarkers] = useState(() => parseInitial(initial));
    const [localMarkers, setLocalMarkers] = useState([]);

    useEffect(() => {
        setBaseMarkers(parseInitial(initial));
    }, [initial, parseInitial]);

    const markers = useMemo(
        () => [...baseMarkers, ...localMarkers],
        [baseMarkers, localMarkers]
    );

    useEffect(() => {
        if (typeof onChange === 'function') onChange(markers);
    }, [markers, onChange]);

    const add = useCallback((x, y) => {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        setLocalMarkers((prev) => [
            ...prev,
            { id: `local-${Date.now()}-${Math.random()}`, x, y, type: 'OW' },
        ]);
    }, []);

    const remove = useCallback((id) => {
        setLocalMarkers((prev) => prev.filter((m) => m.id !== id));
        setBaseMarkers((prev) => prev.filter((m) => m.id !== id));
    }, []);

    return { markers, add, remove };
};

const Legend = React.memo(function Legend() {
    return (
        <div
            style={{
                position: 'absolute',
                left: 12,
                top: 12,
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 8,
                padding: '12px 16px',
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                zIndex: 10,
                fontSize: 12,
                lineHeight: 1.4,
            }}
            aria-label="legend"
        >
            <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#374151' }}>
                設備圖例
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: STATUS_COLORS.default,
                            border: '2px solid #fff',
                            boxShadow: `0 0 0 1px ${THEME.statusRings.default}`,
                        }}
                    />
                    <span style={{ color: '#374151' }}>OW 觀測井</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: 0,
                            background: STATUS_COLORS.default,
                            border: '2px solid #fff',
                            boxShadow: `0 0 0 1px ${THEME.statusRings.default}`,
                        }}
                    />
                    <span style={{ color: '#374151' }}>ALC 自動化監測</span>
                </div>
            </div>
            <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: 'bold', marginBottom: 6, color: '#374151' }}>
                    狀態顏色
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {[
                        ['正常', STATUS_COLORS.green],
                        ['警戒', STATUS_COLORS.yellow],
                        ['行動', STATUS_COLORS.red],
                        ['離線', STATUS_COLORS.gray],
                    ].map(([label, bg]) => (
                        <div
                            key={label}
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                            <div
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: bg,
                                }}
                            />
                            <span style={{ color: '#374151' }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// Coordinate helpers
// ─────────────────────────────────────────────────────────────────────────────
const useImageCoords = (imgRef, ready) => {
    const clientToContent = useCallback(
        (clientX, clientY, wrapperComponent, transformState) => {
            if (!wrapperComponent || !transformState) return null;
            const rect = wrapperComponent.getBoundingClientRect();
            const { scale, positionX, positionY } = transformState;
            return {
                x: (clientX - rect.left - positionX) / scale,
                y: (clientY - rect.top - positionY) / scale,
            };
        },
        []
    );

    const contentToImage = useCallback(
        (contentX, contentY) => {
            const img = imgRef.current;
            if (!img || !ready) return null;

            const container = img.parentElement;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imgWidth = img.clientWidth;
            const imgHeight = img.clientHeight;

            const offsetX = (containerWidth - imgWidth) / 2;
            const offsetY = (containerHeight - imgHeight) / 2;

            if (
                contentX < offsetX ||
                contentX > offsetX + imgWidth ||
                contentY < offsetY ||
                contentY > offsetY + imgHeight
            ) {
                return null;
            }

            const relativeX = (contentX - offsetX) / imgWidth;
            const relativeY = (contentY - offsetY) / imgHeight;

            return { x: relativeX * img.naturalWidth, y: relativeY * img.naturalHeight };
        },
        [imgRef, ready]
    );

    const imageToPercent = useCallback(
        (ix, iy) => {
            const img = imgRef.current;
            if (!img || !img.naturalWidth || !img.naturalHeight)
                return { left: '0%', top: '0%' };
            return {
                left: `${(ix / img.naturalWidth) * 100}%`,
                top: `${(iy / img.naturalHeight) * 100}%`,
            };
        },
        [imgRef]
    );

    return { clientToContent, contentToImage, imageToPercent };
};

// ─────────────────────────────────────────────────────────────────────────────
// Label layout utilities
// ─────────────────────────────────────────────────────────────────────────────
const labelRectFor = (marker, offset, dims = LABEL_DIMENSIONS) => {
    const cx = marker.x + offset.x;
    const cy = marker.y + offset.y;
    const halfW = dims.width / 2;
    const halfH = dims.height / 2;
    return {
        left: cx - halfW,
        right: cx + halfW,
        top: cy - halfH,
        bottom: cy + halfH,
        centerX: cx,
        centerY: cy,
        width: dims.width,
        height: dims.height,
    };
};

const anchorRectFor = (marker) => {
    const half = ANCHOR_BOX.size / 2 + ANCHOR_BOX.padding;
    return {
        left: marker.x - half,
        right: marker.x + half,
        top: marker.y - half,
        bottom: marker.y + half,
        centerX: marker.x,
        centerY: marker.y,
        width: half * 2,
        height: half * 2,
    };
};

const intersects = (a, b, pad = 0) =>
    !(
        a.right < b.left - pad ||
        a.left > b.right + pad ||
        a.bottom < b.top - pad ||
        a.top > b.bottom + pad
    );

const intersectionArea = (a, b) => {
    const xOverlap = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
    const yOverlap = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
    return xOverlap * yOverlap;
};

const scoreCandidate = (candidateRect, marker, placedRects, anchorRects) => {
    const GAP = LABEL_DIMENSIONS.minGap;
    let hardCollision = false;
    let overlapSum = 0;

    for (const r of placedRects) {
        if (intersects(candidateRect, r, GAP)) {
            hardCollision = true;
            overlapSum += intersectionArea(candidateRect, r);
        }
    }
    for (const a of anchorRects) {
        if (intersects(candidateRect, a, GAP)) {
            hardCollision = true;
            overlapSum += intersectionArea(candidateRect, a);
        }
    }

    const dx = candidateRect.centerX - marker.x;
    const dy = candidateRect.centerY - marker.y;
    const dist = Math.hypot(dx, dy);

    const HARD = hardCollision ? 1e9 : 0;
    return HARD + overlapSum * 20 + dist * 0.8;
};

const resolveBestOffset = (marker, placedRects, anchorRects) => {
    const RADS = [60, 80, 100, 120, 140, 170];
    const DIRS = Array.from({ length: 16 }, (_, i) => {
        const theta = (Math.PI * 2 * i) / 16;
        return { x: Math.cos(theta), y: Math.sin(theta) };
    });

    let best = {
        cost: Infinity,
        offset: { x: 0, y: 0 },
        rect: labelRectFor(marker, { x: 0, y: 0 }),
    };

    for (const r of RADS) {
        for (const d of DIRS) {
            const offset = { x: Math.round(d.x * r), y: Math.round(d.y * r) };
            const rect = labelRectFor(marker, offset);
            const cost = scoreCandidate(rect, marker, placedRects, anchorRects);
            if (cost < best.cost) best = { cost, offset, rect };
        }
        if (best.cost < 1e6) break; // found a non-hard-collision placement at current radius
    }

    if (!Number.isFinite(best.cost)) {
        const fallback = { x: 80, y: 0 };
        return { offset: fallback, rect: labelRectFor(marker, fallback) };
    }
    return best;
};

const layoutLabelOffsets = (markers) => {
    const offsets = [];
    const placedRects = [];
    const anchorRects = markers.map(anchorRectFor);
    for (let i = 0; i < markers.length; i++) {
        const m = markers[i];
        const { offset, rect } = resolveBestOffset(m, placedRects, anchorRects);
        offsets.push(offset);
        placedRects.push(rect);
    }
    return offsets;
};

// ─────────────────────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────────────────────
const Marker = React.memo(
    function Marker({
        left,
        top,
        x,
        y,
        name,
        type,
        index,
        labelOffset,
        hovered,
        onHover,
        onLeave,
        markerId,
        alertStatus,
        statusByName,
        onClick,
        markerScale = 1,
    }) {
        const isALC = type === 'ALC' || name?.includes('ALC');
        const offset = labelOffset || { x: 0, y: 0 };
        const { color, ring, status } = getMarkerColors(
            name,
            alertStatus,
            statusByName
        );
        const blinkClass =
            status === 'red' ? 'blink-red' : status === 'yellow' ? 'blink-yellow' : '';

        return (
            <div
                style={{
                    position: 'absolute',
                    left,
                    top,
                    transform: `translate(-50%, -50%) scale(${markerScale})`,
                    transformOrigin: 'center center',
                    pointerEvents: 'none',
                    zIndex: hovered ? 5000 : 1000,
                }}
            >
                <svg
                    style={{
                        position: 'absolute',
                        left: Math.min(offset.x, 0) - 15,
                        top: Math.min(offset.y, 0) - 15,
                        width: Math.abs(offset.x) + 60,
                        height: Math.abs(offset.y) + 60,
                        pointerEvents: 'none',
                        overflow: 'visible',
                    }}
                >
                    <defs>
                        <marker
                            id={`arrowhead-${index}`}
                            markerWidth="8"
                            markerHeight="8"
                            refX="7"
                            refY="2.5"
                            orient="auto"
                            markerUnits="strokeWidth"
                        >
                            <polygon points="0,0 0,5 7,2.5" fill="rgba(0,0,0,0.5)" />
                        </marker>
                    </defs>
                    <line
                        x1={Math.abs(Math.min(offset.x, 0)) + 30}
                        y1={Math.abs(Math.min(offset.y, 0)) + 30}
                        x2={Math.abs(Math.min(offset.x, 0)) + 30 + offset.x}
                        y2={Math.abs(Math.min(offset.y, 0)) + 30 + offset.y}
                        stroke="rgba(0,0,0,0.4)"
                        strokeWidth="1.5"
                        strokeDasharray="3,3"
                        markerEnd={`url(#arrowhead-${index})`}
                    />
                </svg>

                <div
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                    onClick={onClick}
                    className={blinkClass}
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: isALC ? 0 : '50%',
                        background: color,
                        border: '1.5px solid #fff',
                        boxShadow: `0 0 0 2px ${ring}`,
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 2,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                    }}
                    title={`${name ?? ''} (${Math.round(x)}, ${Math.round(y)})`}
                    aria-label={`marker ${name ?? ''}`}
                />

                <div
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                    onClick={onClick}
                    style={{
                        position: 'absolute',
                        left: offset.x,
                        top: offset.y,
                        transform: 'translate(-50%, -50%)',
                        padding: '6px 9px',
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.98)',
                        color: '#374151',
                        fontSize: 11,
                        lineHeight: 1.2,
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontWeight: 500,
                        border: `2px solid ${color}`,
                        boxShadow: hovered
                            ? '0 6px 18px rgba(0,0,0,0.25)'
                            : '0 4px 12px rgba(0,0,0,0.15)',
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        zIndex: 3,
                        minWidth: '52px',
                        textAlign: 'center',
                    }}
                    title={`座標: (${Math.round(x)}, ${Math.round(y)})`}
                >
                    <div style={{ fontWeight: 600 }}>{name}</div>
                </div>
            </div>
        );
    },
    (prev, next) => {
        return (
            prev.left === next.left &&
            prev.top === next.top &&
            prev.x === next.x &&
            prev.y === next.y &&
            prev.name === next.name &&
            prev.type === next.type &&
            prev.index === next.index &&
            prev.hovered === next.hovered &&
            prev.markerScale === next.markerScale &&
            prev.alertStatus === next.alertStatus && // rely on referential equality upstream
            JSON.stringify(prev.labelOffset) === JSON.stringify(next.labelOffset)
        );
    }
);

const ControlPanel = React.memo(function ControlPanel({
    onZoomIn,
    onZoomOut,
    onReset,
    lockCenter,
    onToggleLock,
    onCenterToLastClick,
    onAddMarker,
    hasLastPoint,
}) {
    const disabledStyle = { ...styles.button, opacity: 0.5, cursor: 'not-allowed' };
    return (
        <div style={styles.controlPanel} aria-label="controls">
            <button
                onClick={onZoomIn}
                title="放大"
                style={lockCenter ? disabledStyle : styles.button}
                disabled={lockCenter}
                aria-disabled={lockCenter}
            >
                ＋
            </button>
            <button
                onClick={onZoomOut}
                title="縮小"
                style={lockCenter ? disabledStyle : styles.button}
                disabled={lockCenter}
                aria-disabled={lockCenter}
            >
                －
            </button>
            <button
                onClick={onReset}
                title={lockCenter ? '回到預設中心和縮放' : '回到中心'}
                style={styles.button}
            >
                ⟳
            </button>
            <button
                onClick={onToggleLock}
                title={
                    lockCenter
                        ? '解除鎖定（啟用縮放和拖動）'
                        : '鎖定視圖（禁用縮放和拖動）'
                }
                style={{
                    ...styles.button,
                    ...styles.lockButton,
                    background: lockCenter ? '#1f2937' : '#4b5563',
                }}
            >
                {lockCenter ? '🔒' : '🔓'}
            </button>
            {/* <button
                onClick={onAddMarker}
                disabled={!hasLastPoint}
                title="在最近點擊位置加標記"
                style={hasLastPoint ? styles.button : disabledStyle}
                aria-disabled={!hasLastPoint}
            >
                📍
            </button> */}
        </div>
    );
});

// Helper to safely lift utils out of render-prop without setting state during render

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function ZoomableImagePro({
    image,
    height = DEFAULT_CONFIG.height,
    minScale = DEFAULT_CONFIG.minScale,
    maxScale = DEFAULT_CONFIG.maxScale,
    step = DEFAULT_CONFIG.step,
    defaultCenter = null,
    deviceLoc = [],
    alertStatus = [],
    statusByName,
    onMarkersChange,
    onMarkerClick, // optional
    enableCoordinateDebug = false,
}) {
    const [lockCenter, setLockCenter] = useState(true);
    const [imgReady, setImgReady] = useState(false);
    const [lastPoint, setLastPoint] = useState(null);
    const [transformUtils, setTransformUtils] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [currentZoomScale, setCurrentZoomScale] = useState(
        DEFAULT_CONFIG.initialScale
    );
    const utilsRef = useRef(null);
    const navigate = useNavigate();
    useEffect(() => {
        injectBlinkStyles();
    }, []);

    const imgRef = useRef(null);
    const { markers, add } = useMarkers(deviceLoc, onMarkersChange);
    const visibleMarkers = useMemo(
        () =>
            (markers || []).filter((m) => Number.isFinite(m?.x) && Number.isFinite(m?.y)),
        [markers]
    );
    const { clientToContent, contentToImage, imageToPercent } = useImageCoords(
        imgRef,
        imgReady
    );

    // Precompute label offsets
    const labelOffsets = useMemo(
        () => (visibleMarkers?.length ? layoutLabelOffsets(visibleMarkers) : []),
        [visibleMarkers]
    );

    // Center helpers
    const centerToPoint = useCallback((imgX, imgY) => {
        const u = utilsRef.current;
        if (!u || !imgRef.current) return;

        const img = imgRef.current;
        const { instance } = u;
        const wrapperRect = instance.wrapperComponent.getBoundingClientRect();

        const scaleX = img.clientWidth / img.naturalWidth;
        const scaleY = img.clientHeight / img.naturalHeight;
        const scale = instance.transformState.scale;

        const pointX = imgX * scaleX * scale;
        const pointY = imgY * scaleY * scale;

        const centerX = wrapperRect.width / 2;
        const centerY = wrapperRect.height / 2;

        u.setTransform(
            centerX - pointX,
            centerY - pointY,
            scale,
            DEFAULT_CONFIG.animationDuration
        );
    }, []);

    const resetToDefault = useCallback(() => {
        const u = utilsRef.current;
        if (!u) return;
        if (defaultCenter && imgReady) {
            u.setTransform(0, 0, DEFAULT_CONFIG.initialScale, 0);
            // micro delay to ensure image metrics ready
            setTimeout(() => centerToPoint(defaultCenter.x, defaultCenter.y), 50);
        } else {
            u.resetTransform(DEFAULT_CONFIG.animationDuration);
        }
    }, [defaultCenter, imgReady, centerToPoint]);

    useEffect(() => {
        if (!defaultCenter || !imgReady || !transformUtils) return;
        const timer = setTimeout(() => resetToDefault(), 100);
        return () => clearTimeout(timer);
    }, [defaultCenter, imgReady, transformUtils, resetToDefault]);

    // Pointer → image coords
    const handlePointerDown = useCallback(
        (e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            const u = utilsRef.current;
            if (!u) return;
            const { instance } = u;
            if (!instance?.wrapperComponent || !instance?.transformState) return;

            const content = clientToContent(
                e.clientX,
                e.clientY,
                instance.wrapperComponent,
                instance.transformState
            );
            if (!content) return;

            const imgCoords = contentToImage(content.x, content.y);
            if (!imgCoords) return;

            const img = imgRef.current;
            if (!img) return;

            const clampedX = Math.max(
                0,
                Math.min(Math.round(imgCoords.x), img.naturalWidth)
            );
            const clampedY = Math.max(
                0,
                Math.min(Math.round(imgCoords.y), img.naturalHeight)
            );
            setLastPoint({ x: clampedX, y: clampedY });
        },
        [clientToContent, contentToImage]
    );

    // Controls
    const handleZoomIn = useCallback(() => {
        if (lockCenter) return;
        utilsRef.current?.zoomIn(step, DEFAULT_CONFIG.animationDuration);
    }, [lockCenter, step]);

    const handleZoomOut = useCallback(() => {
        if (lockCenter) return;
        utilsRef.current?.zoomOut(step, DEFAULT_CONFIG.animationDuration);
    }, [lockCenter, step]);

    const handleReset = useCallback(() => {
        if (lockCenter) {
            resetToDefault();
        } else if (defaultCenter && imgReady) {
            centerToPoint(defaultCenter.x, defaultCenter.y);
        } else {
            utilsRef.current?.resetTransform(DEFAULT_CONFIG.animationDuration);
        }
    }, [lockCenter, defaultCenter, imgReady, resetToDefault, centerToPoint]);

    const handleCenterToLastClick = useCallback(() => {
        if (!lastPoint || lockCenter) return;
        centerToPoint(lastPoint.x, lastPoint.y);
    }, [lastPoint, lockCenter, centerToPoint]);

    const handleAddMarkerFromLastClick = useCallback(() => {
        if (lastPoint) add(lastPoint.x, lastPoint.y);
    }, [lastPoint, add]);

    const handleToggleLock = useCallback(() => setLockCenter((prev) => !prev), []);

    const handleHoverEnter = useCallback((id) => setHoveredId(id), []);
    const handleHoverLeave = useCallback(() => setHoveredId(null), []);

    const handleMarkerClick = useCallback(
        (marker) => {
            if (onMarkerClick) {
                onMarkerClick(marker);
            } else {
                // Default navigation: navigate to a device page based on marker name
                navigate(`/device/${marker.name}`);
            }
        },
        [navigate, onMarkerClick]
    );
    const markerScale = useMemo(() => {
        const safeScale =
            Number.isFinite(currentZoomScale) && currentZoomScale > 0
                ? currentZoomScale
                : DEFAULT_CONFIG.initialScale;
        return Math.max(0.35, Math.min(1, DEFAULT_CONFIG.initialScale / safeScale));
    }, [currentZoomScale]);

    return (
        <div style={{ ...styles.container, height }}>
            <TransformWrapper
                onInit={(ref) => {
                    if (ref && utilsRef.current !== ref) {
                        utilsRef.current = ref;
                        setTransformUtils(ref);
                        const initialScale = ref?.instance?.transformState?.scale;
                        if (Number.isFinite(initialScale) && initialScale > 0) {
                            setCurrentZoomScale(initialScale);
                        }
                    }
                }}
                onTransformed={(ref) => {
                    const scale = ref?.instance?.transformState?.scale;
                    if (Number.isFinite(scale) && scale > 0) {
                        setCurrentZoomScale(scale);
                    }
                }}
                initialScale={DEFAULT_CONFIG.initialScale}
                minScale={minScale}
                maxScale={maxScale}
                wheel={{ disabled: lockCenter, step }}
                doubleClick={{ disabled: true }}
                pinch={{ disabled: lockCenter }}
                panning={{ disabled: lockCenter }}
                limitToBounds={false}
                centerZoomedOut
                centerOnInit
            >
                {() => (
                    <>
                        <TransformComponent
                            wrapperStyle={styles.wrapper}
                            contentStyle={styles.content}
                        >
                            <img
                                ref={imgRef}
                                src={image}
                                alt="zoomable content"
                                draggable={false}
                                onLoad={() => setImgReady(true)}
                                style={styles.image}
                            />
                            <div
                                onPointerDown={handlePointerDown}
                                style={styles.clickOverlay}
                            />

                            {imgReady &&
                                visibleMarkers.map((m, index) => {
                                    const { left, top } = imageToPercent(m.x, m.y);
                                    const offset = labelOffsets[index] || { x: 0, y: 0 };
                                    return (
                                        <Marker
                                            key={m.id}
                                            left={left}
                                            top={top}
                                            x={m.x}
                                            y={m.y}
                                            name={m.name}
                                            type={m.type}
                                            index={index}
                                            labelOffset={offset}
                                            hovered={hoveredId === m.id}
                                            onHover={() => handleHoverEnter(m.id)}
                                            onLeave={handleHoverLeave}
                                            markerId={m.id}
                                            alertStatus={alertStatus}
                                            statusByName={statusByName}
                                            onClick={() => handleMarkerClick(m)}
                                            markerScale={markerScale}
                                        />
                                    );
                                })}
                        </TransformComponent>

                        <Legend />

                        {enableCoordinateDebug && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 12,
                                    bottom: 12,
                                    zIndex: 11,
                                    background: 'rgba(17,24,39,0.86)',
                                    color: '#f9fafb',
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    lineHeight: 1.4,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                }}
                            >
                                {lastPoint
                                    ? `點擊座標: (${lastPoint.x}, ${lastPoint.y})`
                                    : '點擊地圖以顯示座標'}
                            </div>
                        )}

                        <ControlPanel
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            onReset={handleReset}
                            lockCenter={lockCenter}
                            onToggleLock={handleToggleLock}
                            onCenterToLastClick={handleCenterToLastClick}
                            onAddMarker={handleAddMarkerFromLastClick}
                            hasLastPoint={!!lastPoint}
                        />
                    </>
                )}
            </TransformWrapper>
        </div>
    );
}
